import { Spin } from 'antd';
import { FC, useEffect } from 'react';
import {
  RouterProvider,
  createBrowserRouter,
  useRouteError,
} from 'react-router-dom';

import { useAccount, useMatomoUser } from '@hooks';
import { env } from '@utils/env';
import { trackPageView } from '@utils/matomo';

import { ErrorFallback } from './ErrorFallback';

function DefaultErrorComponent() {
  const error = useRouteError();
  const stack = error instanceof Error ? error.stack : undefined;

  return <ErrorFallback error={error} componentStack={stack} />;
}

const AppRouter: FC = () => {
  const { routerPaths } = useAccount();

  // Keep the Matomo user context in sync with auth (no-op when disabled).
  useMatomoUser();

  const appRoutes = [
    {
      path: '/',
      errorElement: <DefaultErrorComponent />,
      children: routerPaths,
    },
  ];

  appRoutes.forEach((route) => {
    route.children?.forEach((child) => {
      child.errorElement = <DefaultErrorComponent />;
      child.children?.forEach((child) => {
        child.errorElement = <DefaultErrorComponent />;
      });
    });
  });

  const router = createBrowserRouter(appRoutes, {
    basename: env.VITE_ROUTE_BASE_URL || '/',
  });

  // Report SPA route changes to Matomo. Runs only when navigation settles, and
  // trackPageView() no-ops when analytics is disabled and dedupes repeat paths.
  useEffect(() => {
    const unsubscribe = router.subscribe((state) => {
      if (state.navigation.state === 'idle') {
        trackPageView(state.location.pathname + state.location.search);
      }
    });

    return unsubscribe;
  }, [router]);

  return <RouterProvider router={router} fallbackElement={<Spin />} />;
};

export default AppRouter;
